/**
 * FleetManager - Shared Data Hooks
 * React Query hooks for fetching data from the API
 *
 * Uses centralized query configuration from lib/queryConfig.js
 * for consistent caching, query keys, and invalidation patterns.
 *
 * @module hooks/useDataHooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  vehicleAPI,
  driverAPI,
  claimAPI,
  maintenanceAPI,
  insuranceAPI,
  fuelLogAPI,
  inspectionAPI,
  vehicleAssignmentAPI,
  organizationAPI,
  workingSiteAPI,
  tripAPI,
  eventAPI,
  expenseAPI,
  vehicleDocumentAPI
} from '../api.js';
import { queryKeys, queryOptions, invalidationPatterns } from '../lib/queryConfig.js';

// Configuration - set to false for real API
export const USE_MOCK_DATA = false;

// =============================================================================
// VEHICLE HOOKS
// =============================================================================

/**
 * Fetch all vehicles
 * Uses reference cache settings (1 min stale, 10 min gc)
 */
export function useVehicles(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.vehicles.list(filters)
      : queryKeys.vehicles.all,
    queryFn: async () => {
      const response = await vehicleAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.reference,
  });
}

/**
 * Fetch active vehicles only (excludes retired/exited)
 *
 * USE THIS for all <select> dropdowns, form pickers, and assignment UIs.
 * Use useVehicles() only when you need the full list including retired (e.g., admin, archive views).
 */
export function useActiveVehicles() {
  return useQuery({
    queryKey: [...queryKeys.vehicles.all, 'active'],
    queryFn: async () => {
      const response = await vehicleAPI.getAll();
      const all = response.data || [];
      return all.filter(v => v.status !== 'retired' && v.status !== 'exited');
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.reference,
  });
}

/**
 * Fetch single vehicle by ID
 */
export function useVehicle(id) {
  return useQuery({
    queryKey: queryKeys.vehicles.detail(id),
    queryFn: async () => {
      const response = await vehicleAPI.getById(id);
      return response.data;
    },
    enabled: !USE_MOCK_DATA && !!id,
    ...queryOptions.reference,
  });
}

/**
 * Mutation hook for creating a vehicle
 */
export function useCreateVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => vehicleAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(invalidationPatterns.vehicleCreated);
    },
  });
}

/**
 * Mutation hook for updating a vehicle
 */
export function useUpdateVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => vehicleAPI.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(invalidationPatterns.vehicleModified(id));
      queryClient.invalidateQueries(invalidationPatterns.listInvalidation('vehicles'));
    },
  });
}

/**
 * Mutation hook for deleting a vehicle
 */
export function useDeleteVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => vehicleAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(invalidationPatterns.vehicleCreated);
    },
  });
}

// =============================================================================
// DRIVER HOOKS
// =============================================================================

/**
 * Fetch all drivers
 * Uses reference cache settings (1 min stale, 10 min gc)
 */
export function useDrivers(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.drivers.list(filters)
      : queryKeys.drivers.all,
    queryFn: async () => {
      const response = await driverAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.reference,
  });
}

/**
 * Fetch active drivers only (excludes inactive/terminated)
 *
 * USE THIS for all <select> dropdowns, form pickers, and assignment UIs.
 */
export function useActiveDrivers() {
  return useQuery({
    queryKey: [...queryKeys.drivers.all, 'active'],
    queryFn: async () => {
      const response = await driverAPI.getAll();
      const all = response.data || [];
      return all.filter(d => d.status !== 'inactive' && d.status !== 'terminated');
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.reference,
  });
}

/**
 * Fetch single driver by ID
 */
export function useDriver(id) {
  return useQuery({
    queryKey: queryKeys.drivers.detail(id),
    queryFn: async () => {
      const response = await driverAPI.getById(id);
      return response.data;
    },
    enabled: !USE_MOCK_DATA && !!id,
    ...queryOptions.reference,
  });
}

/**
 * Mutation hook for creating a driver
 */
export function useCreateDriver() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => driverAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
    },
  });
}

/**
 * Mutation hook for updating a driver
 */
export function useUpdateDriver() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => driverAPI.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(invalidationPatterns.driverModified(id));
      queryClient.invalidateQueries(invalidationPatterns.listInvalidation('drivers'));
    },
  });
}

// =============================================================================
// CLAIMS HOOKS
// =============================================================================

/**
 * Fetch all claims
 * Uses transactional cache settings (30s stale, 5 min gc)
 */
export function useClaims(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.claims.list(filters)
      : queryKeys.claims.all,
    queryFn: async () => {
      const response = await claimAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.transactional,
  });
}

/**
 * Mutation hook for updating claim status
 */
export function useUpdateClaimStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, data }) => claimAPI.update(id, { status, ...data }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(invalidationPatterns.claimModified(id));
      queryClient.invalidateQueries(invalidationPatterns.listInvalidation('claims'));
    },
  });
}

// =============================================================================
// MAINTENANCE HOOKS
// =============================================================================

/**
 * Fetch all maintenance requests
 * Uses transactional cache settings (30s stale, 5 min gc)
 */
export function useMaintenance(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.maintenance.list(filters)
      : queryKeys.maintenance.all,
    queryFn: async () => {
      const response = await maintenanceAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.transactional,
  });
}

/**
 * Mutation hook for updating maintenance status
 */
export function useUpdateMaintenanceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, data }) => maintenanceAPI.update(id, { status, ...data }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(invalidationPatterns.maintenanceModified(id));
      queryClient.invalidateQueries(invalidationPatterns.listInvalidation('maintenance'));
    },
  });
}

// =============================================================================
// INSURANCE HOOKS
// =============================================================================

/**
 * Fetch all insurance contracts
 * Uses transactional cache settings (30s stale, 5 min gc)
 */
export function useInsurance(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.insurance.list(filters)
      : queryKeys.insurance.all,
    queryFn: async () => {
      const response = await insuranceAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.transactional,
  });
}

// =============================================================================
// FUEL LOGS HOOKS
// =============================================================================

/**
 * Fetch all fuel logs
 * Uses transactional cache settings (30s stale, 5 min gc)
 */
export function useFuelLogs(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.fuelLogs.list(filters)
      : queryKeys.fuelLogs.all,
    queryFn: async () => {
      const response = await fuelLogAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.transactional,
  });
}

// =============================================================================
// INSPECTIONS HOOKS
// =============================================================================

/**
 * Fetch all inspections
 * Uses transactional cache settings (30s stale, 5 min gc)
 */
export function useInspections(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.inspections.list(filters)
      : queryKeys.inspections.all,
    queryFn: async () => {
      const response = await inspectionAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.transactional,
  });
}

// =============================================================================
// ASSIGNMENTS HOOKS
// =============================================================================

/**
 * Fetch all assignments
 * Uses reference cache settings (1 min stale, 10 min gc)
 */
export function useAssignments() {
  return useQuery({
    queryKey: queryKeys.assignments.all,
    queryFn: async () => {
      const response = await vehicleAssignmentAPI.getAll();
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.reference,
  });
}

// =============================================================================
// ORGANIZATIONS HOOKS (STATIC DATA)
// =============================================================================

/**
 * Fetch all organizations
 * Uses static cache settings (5 min stale, 30 min gc)
 */
export function useOrganizations() {
  return useQuery({
    queryKey: queryKeys.organizations.all,
    queryFn: async () => {
      const response = await organizationAPI.getAll();
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.static,
  });
}

// =============================================================================
// WORKING SITES HOOKS (STATIC DATA)
// =============================================================================

/**
 * Fetch all working sites
 * Uses static cache settings (5 min stale, 30 min gc)
 */
export function useWorkingSites() {
  return useQuery({
    queryKey: queryKeys.workingSites.all,
    queryFn: async () => {
      const response = await workingSiteAPI.getAll();
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.static,
  });
}

// =============================================================================
// TRIPS HOOKS
// =============================================================================

/**
 * Fetch all trips
 * Uses transactional cache settings (30s stale, 5 min gc)
 */
export function useTrips(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.trips.list(filters)
      : queryKeys.trips.all,
    queryFn: async () => {
      const response = await tripAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.transactional,
  });
}

// =============================================================================
// EVENTS HOOKS
// =============================================================================

/**
 * Fetch all events
 * Uses transactional cache settings (30s stale, 5 min gc)
 */
export function useEvents(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.events.list(filters)
      : queryKeys.events.all,
    queryFn: async () => {
      const response = await eventAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.transactional,
  });
}

/**
 * Fetch single event by ID
 */
export function useEvent(id) {
  return useQuery({
    queryKey: queryKeys.events.detail(id),
    queryFn: async () => {
      const response = await eventAPI.getById(id);
      return response.data;
    },
    enabled: !USE_MOCK_DATA && !!id,
    ...queryOptions.transactional,
  });
}

// =============================================================================
// EXPENSES HOOKS
// =============================================================================

/**
 * Fetch all expenses
 * Uses transactional cache settings (30s stale, 5 min gc)
 */
export function useExpenses(filters = {}) {
  return useQuery({
    queryKey: Object.keys(filters).length > 0
      ? queryKeys.expenses.list(filters)
      : queryKeys.expenses.all,
    queryFn: async () => {
      const response = await expenseAPI.getAll(filters);
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.transactional,
  });
}

/**
 * Fetch single expense by ID
 */
export function useExpense(id) {
  return useQuery({
    queryKey: queryKeys.expenses.detail(id),
    queryFn: async () => {
      const response = await expenseAPI.getById(id);
      return response.data;
    },
    enabled: !USE_MOCK_DATA && !!id,
    ...queryOptions.transactional,
  });
}

/**
 * Fetch expenses by event ID
 */
export function useExpensesByEvent(eventId) {
  return useQuery({
    queryKey: ['expenses', 'byEvent', eventId],
    queryFn: async () => {
      const response = await expenseAPI.getAll({ event_id: eventId });
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA && !!eventId,
    ...queryOptions.transactional,
  });
}

// =============================================================================
// DOCUMENTS HOOKS
// =============================================================================

/**
 * Fetch all documents
 * Uses reference cache settings (1 min stale, 10 min gc)
 */
export function useDocuments() {
  return useQuery({
    queryKey: queryKeys.documents.all,
    queryFn: async () => {
      const response = await vehicleDocumentAPI.getAll();
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.reference,
  });
}

/**
 * Fetch all vehicle documents
 */
export function useAllVehicleDocuments() {
  return useQuery({
    queryKey: ['allVehicleDocuments'],
    queryFn: async () => {
      const response = await vehicleDocumentAPI.getAll();
      return response.data || [];
    },
    enabled: !USE_MOCK_DATA,
    ...queryOptions.reference,
  });
}

// =============================================================================
// UTILITY HOOK FOR CACHE MANAGEMENT
// =============================================================================

/**
 * Hook to access query client for manual cache operations
 * @returns {import('@tanstack/react-query').QueryClient} Query client instance
 * @example
 * const queryClient = useFleetQueryClient();
 * // Invalidate all vehicles
 * queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
 */
export function useFleetQueryClient() {
  return useQueryClient();
}

// Re-export query keys and options for component-level usage
export { queryKeys, queryOptions, invalidationPatterns };
